import axios from 'axios';
import {
  apiUrl,
  certificadoAlumnosClase,
  certificadoCancelarEmisionBorrador,
  certificadoCancelarLote,
  certificadoDescargarLote,
  certificadoLote,
  certificadoLotes,
  certificadoPublicarLote,
  certificadoPreview,
  certificadoProcesarLote,
  certificadoReintentarLote,
  certificadoValidar,
  misCertificados,
} from '../Components/Pages/constants';

export const certificateDownloadUrl = (downloadUrl) => {
  if (typeof downloadUrl !== 'string' || !downloadUrl.trim()) return '#';
  if (/^https?:\/\//i.test(downloadUrl)) return downloadUrl;
  const path = downloadUrl.startsWith('/') ? downloadUrl : '/' + downloadUrl;
  return apiUrl + path;
};

const STABLE_ERROR_CODES = new Set([
  'ALREADY_PROCESSED',
  'BATCH_CANCELLED',
  'BATCH_LAST_CERTIFICATE',
  'BATCH_NOT_FOUND',
  'BATCH_NOT_READY',
  'CERTIFICATE_CANCELLED',
  'CERTIFICATE_NOT_FOUND',
  'PDF_RENDER_ERROR',
  'PDF_STORAGE_ERROR',
  'STUDENT_NOT_ENROLLED',
  'STALE_BATCH',
  'VALIDATION_ERROR',
]);

const NETWORK_ERROR_MESSAGE =
  'No fue posible comunicarse con el servicio de constancias';
const REQUEST_ERROR_MESSAGE =
  'No fue posible completar la operación de constancias';

export class CertificateApiError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = 'CertificateApiError';
    this.code = code;
    this.status = status;
  }
}

function isBlob(value) {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

async function readErrorPayload(error) {
  const responseData = error?.response?.data;

  if (!isBlob(responseData)) {
    return responseData;
  }

  if (
    typeof responseData.text !== 'function' ||
    !responseData.type?.toLowerCase().includes('json')
  ) {
    return null;
  }

  try {
    return JSON.parse(await responseData.text());
  } catch (_error) {
    return null;
  }
}

async function normalizeError(error) {
  const payload = await readErrorPayload(error);
  const backendError = payload?.error;
  const code = backendError?.code;
  const message = backendError?.message;
  const status = Number.isInteger(error?.response?.status)
    ? error.response.status
    : null;

  if (
    STABLE_ERROR_CODES.has(code) &&
    typeof message === 'string' &&
    message.trim()
  ) {
    return new CertificateApiError(code, message.trim(), status);
  }

  if (!error?.response) {
    return new CertificateApiError(
      'NETWORK_ERROR',
      NETWORK_ERROR_MESSAGE,
      null,
    );
  }

  return new CertificateApiError(
    'REQUEST_ERROR',
    REQUEST_ERROR_MESSAGE,
    status,
  );
}

function withNormalizedErrors(request) {
  return request.catch(async (error) => {
    throw await normalizeError(error);
  });
}

function unwrapJson(request) {
  return withNormalizedErrors(request).then(({ data }) => data.data);
}

function unwrapBlob(request) {
  return withNormalizedErrors(request).then(({ data }) => data);
}

export const certificateApi = {
  listStudents: (idClase) =>
    unwrapJson(axios.get(certificadoAlumnosClase(idClase))),
  preview: (payload) =>
    unwrapBlob(
      axios.post(certificadoPreview, payload, { responseType: 'blob' }),
    ),
  createBatch: (payload) =>
    unwrapJson(axios.post(certificadoLotes, payload)),
  listBatches: (publicationState = 'borrador') =>
    unwrapJson(
      axios.get(certificadoLotes, {
        params: { publicacion: publicationState },
      }),
    ),
  getBatch: (idLote) => unwrapJson(axios.get(certificadoLote(idLote))),
  processChunk: (idLote) =>
    unwrapJson(
      axios.post(certificadoProcesarLote(idLote), { limit: 5 }),
    ),
  retryFailed: (idLote) =>
    unwrapJson(axios.post(certificadoReintentarLote(idLote))),
  batchDownloadUrl: (idLote) =>
    certificadoDescargarLote(idLote),
  publishBatch: (idLote) =>
    unwrapJson(axios.post(certificadoPublicarLote(idLote))),
  cancelBatch: (idLote) =>
    unwrapJson(axios.post(certificadoCancelarLote(idLote))),
  cancelDraftIssuance: (idLote, idEmision) =>
    unwrapJson(
      axios.post(certificadoCancelarEmisionBorrador(idLote, idEmision)),
    ),
  validate: (token) => unwrapJson(axios.get(certificadoValidar(token))),
  listMine: (matricula) => unwrapJson(axios.get(misCertificados(matricula))),
};
